import {
  Dialog,
  Flex,
  Text,
  Button,
  Badge,
} from '@radix-ui/themes';
import { CheckCircledIcon } from '@radix-ui/react-icons';

interface SlackInstallationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slackInstallation: {
    id: string;
    name?: string | null;
    botUserId: string;
    botUserName?: string | null;
    slackTeamId: string;
    teamDomain?: string | null;
    tokenScopes?: string[] | null;
    isActive: boolean;
    createdAt: string;
  } | null;
}

// Slack logo SVG component
const SlackIcon = () => (
  <svg viewBox="0 0 54 54" width="32" height="32">
    <g fill="none">
      <path
        d="M19.712.133a5.381 5.381 0 0 0-5.376 5.387 5.381 5.381 0 0 0 5.376 5.386h5.376V5.52A5.381 5.381 0 0 0 19.712.133m0 14.365H5.376A5.381 5.381 0 0 0 0 19.884a5.381 5.381 0 0 0 5.376 5.387h14.336a5.381 5.381 0 0 0 5.376-5.387 5.381 5.381 0 0 0-5.376-5.386"
        fill="#36C5F0"
      />
      <path
        d="M53.76 19.884a5.381 5.381 0 0 0-5.376-5.386 5.381 5.381 0 0 0-5.376 5.386v5.387h5.376a5.381 5.381 0 0 0 5.376-5.387m-14.336 0V5.52A5.381 5.381 0 0 0 34.048.133a5.381 5.381 0 0 0-5.376 5.387v14.364a5.381 5.381 0 0 0 5.376 5.387 5.381 5.381 0 0 0 5.376-5.387"
        fill="#2EB67D"
      />
      <path
        d="M34.048 54a5.381 5.381 0 0 0 5.376-5.387 5.381 5.381 0 0 0-5.376-5.386h-5.376v5.386A5.381 5.381 0 0 0 34.048 54m0-14.365h14.336a5.381 5.381 0 0 0 5.376-5.386 5.381 5.381 0 0 0-5.376-5.387H34.048a5.381 5.381 0 0 0-5.376 5.387 5.381 5.381 0 0 0 5.376 5.386"
        fill="#ECB22E"
      />
      <path
        d="M0 34.249a5.381 5.381 0 0 0 5.376 5.386 5.381 5.381 0 0 0 5.376-5.386v-5.387H5.376A5.381 5.381 0 0 0 0 34.25m14.336 0v14.364a5.381 5.381 0 0 0 5.376 5.387 5.381 5.381 0 0 0 5.376-5.387V34.25a5.381 5.381 0 0 0-5.376-5.387 5.381 5.381 0 0 0-5.376 5.387"
        fill="#E01E5A"
      />
    </g>
  </svg>
);

export default function SlackInstallationDialog({
  open,
  onOpenChange,
  slackInstallation,
}: SlackInstallationDialogProps) {
  if (!slackInstallation) return null;

  const displayName = slackInstallation.name || slackInstallation.teamDomain || 'Slack Workspace';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content style={{ maxWidth: 420, padding: 0, overflow: 'hidden' }}>
        {/* Header with Slack branding */}
        <Flex
          align="center"
          gap="4"
          style={{
            padding: '24px',
            borderBottom: '1px solid var(--gray-a4)',
          }}
        >
          <div
            style={{
              width: '52px',
              height: '52px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #4a154b22 0%, #4a154b33 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <SlackIcon />
          </div>
          <Flex direction="column" gap="1" style={{ minWidth: 0 }}>
            <Dialog.Title style={{ margin: 0 }}>
              {displayName}
            </Dialog.Title>
            <Flex align="center" gap="2">
              <CheckCircledIcon style={{ color: '#10b981', flexShrink: 0 }} />
              <Text size="2" color="gray">Connected</Text>
            </Flex>
          </Flex>
        </Flex>

        {/* Details */}
        <Flex direction="column" gap="0" style={{ padding: '0' }}>
          <InfoRow label="Workspace" value={slackInstallation.teamDomain || '-'} />
          <InfoRow
            label="Workspace ID"
            value={slackInstallation.slackTeamId}
            mono
          />
          <InfoRow
            label="Bot"
            value={slackInstallation.botUserName || slackInstallation.botUserId}
          />
          <InfoRow
            label="Connected"
            value={new Date(slackInstallation.createdAt).toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          />

          {slackInstallation.tokenScopes && slackInstallation.tokenScopes.length > 0 && (
            <Flex
              direction="column"
              gap="2"
              style={{
                padding: '12px 24px',
                borderTop: '1px solid var(--gray-a4)',
              }}
            >
              <Text size="1" color="gray" weight="medium">
                Permissions
              </Text>
              <Flex gap="1" wrap="wrap">
                {slackInstallation.tokenScopes.map((scope) => (
                  <Badge key={scope} variant="soft" color="gray" size="1">
                    {scope}
                  </Badge>
                ))}
              </Flex>
            </Flex>
          )}
        </Flex>

        {/* Footer */}
        <Flex
          justify="end"
          gap="2"
          style={{
            padding: '16px 24px',
            borderTop: '1px solid var(--gray-a4)',
            backgroundColor: 'var(--gray-a2)',
          }}
        >
          <Dialog.Close>
            <Button variant="soft" color="gray">
              Close
            </Button>
          </Dialog.Close>
        </Flex>

      </Dialog.Content>
    </Dialog.Root>
  );
}

function InfoRow({
  label,
  value,
  mono = false
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <Flex
      justify="between"
      align="center"
      style={{
        padding: '12px 24px',
        borderTop: '1px solid var(--gray-a4)',
      }}
    >
      <Text size="2" color="gray">
        {label}
      </Text>
      <Text
        size="2"
        weight="medium"
        style={mono ? { fontFamily: 'var(--font-mono)', fontSize: '12px' } : undefined}
      >
        {value}
      </Text>
    </Flex>
  );
}
